import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { formatCurrency } from '../utils/format';
import { 
  CreditCard, Plus, Edit2, Trash2, Upload, Eye, X, 
  Calendar, AlertCircle, CheckCircle, RefreshCw, RotateCcw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function KrediKartlari() {
  const { currentUser } = useApp();
  const [kartlar, setKartlar] = useState([]);
  const [bankalar, setBankalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingKart, setEditingKart] = useState(null);
  const [showSilinmisler, setShowSilinmisler] = useState(false);
  const [ozet, setOzet] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    kartAdi: '',
    bankaAdi: '',
    kartNumarasi: '',
    kartLimiti: '',
    hesapKesimGunu: '',
    sonOdemeGunu: '',
    guncelBorc: '',
    asgariOdeme: ''
  });

  useEffect(() => {
    loadData();
  }, [showSilinmisler]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [kartlarRes, bankalarRes, ozetRes] = await Promise.all([
        api.getKrediKartlari({ silinmisler: showSilinmisler }),
        api.getBankalar(),
        api.getKrediKartiOzet()
      ]);
      setKartlar(kartlarRes.data);
      setBankalar(bankalarRes.data);
      setOzet(ozetRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      kartAdi: '',
      bankaAdi: '',
      kartNumarasi: '',
      kartLimiti: '',
      hesapKesimGunu: '',
      sonOdemeGunu: '',
      guncelBorc: '',
      asgariOdeme: ''
    });
    setEditingKart(null);
  };

  const openModal = (kart = null) => {
    if (kart) {
      setEditingKart(kart);
      setFormData({
        kartAdi: kart.kartAdi || '',
        bankaAdi: kart.bankaAdi || '',
        kartNumarasi: kart.kartNumarasi || '',
        kartLimiti: kart.kartLimiti?.toString() || '',
        hesapKesimGunu: kart.hesapKesimGunu?.toString() || '',
        sonOdemeGunu: kart.sonOdemeGunu?.toString() || '',
        guncelBorc: kart.guncelBorc?.toString() || '',
        asgariOdeme: kart.asgariOdeme?.toString() || ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.kartAdi || !formData.bankaAdi || !formData.hesapKesimGunu || !formData.sonOdemeGunu) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    try {
      const data = {
        kartAdi: formData.kartAdi,
        bankaAdi: formData.bankaAdi,
        kartNumarasi: formData.kartNumarasi || null,
        kartLimiti: parseFloat(formData.kartLimiti) || 0,
        hesapKesimGunu: parseInt(formData.hesapKesimGunu),
        sonOdemeGunu: parseInt(formData.sonOdemeGunu),
        guncelBorc: parseFloat(formData.guncelBorc) || 0,
        asgariOdeme: parseFloat(formData.asgariOdeme) || 0,
        girenKullaniciId: currentUser?.id
      };

      if (editingKart) {
        await api.updateKrediKarti(editingKart.id, data);
        toast.success('Kredi kartı güncellendi');
      } else {
        await api.createKrediKarti(data);
        toast.success('Kredi kartı eklendi');
      }

      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (kart) => {
    if (!window.confirm(`"${kart.kartAdi}" kartını silmek istediğinize emin misiniz?`)) return;
    
    try {
      await api.deleteKrediKarti(kart.id, currentUser?.id);
      toast.success('Kredi kartı silindi');
      loadData();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleGeriYukle = async (kart) => {
    try {
      await api.geriYukleKrediKarti(kart.id);
      toast.success('Kredi kartı geri yüklendi');
      loadData();
    } catch (error) {
      toast.error('Geri yükleme başarısız');
    }
  };

  const handleUploadEkstre = async (kartId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await api.uploadKrediKartiEkstre(kartId, formData);
      toast.success('Ekstre yüklendi');
      loadData();
    } catch (error) {
      toast.error('Ekstre yüklenemedi');
    }
  };

  // Gün listesi oluştur
  const gunler = Array.from({ length: 31 }, (_, i) => i + 1);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-textPrimary flex items-center gap-2">
            <CreditCard className="h-5 w-5 md:h-6 md:w-6" />
            Kredi Kartları
          </h1>
          <p className="text-xs md:text-sm text-textSecondary mt-1">
            Kredi kartlarınızı ve ekstre bilgilerinizi yönetin
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={showSilinmisler ? "default" : "outline"}
            onClick={() => setShowSilinmisler(!showSilinmisler)}
            size="sm"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            {showSilinmisler ? 'Aktif Kartlar' : 'Silinenler'}
          </Button>
          <Button onClick={() => openModal()} className="bg-primary">
            <Plus className="h-4 w-4 mr-1" />
            Yeni Kart
          </Button>
        </div>
      </div>

      {/* Özet Kartları */}
      {ozet && !showSilinmisler && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div className="bg-surface border border-border rounded-lg p-3 md:p-4">
            <p className="text-xs text-textSecondary">Toplam Borç</p>
            <p className="text-base md:text-xl font-bold text-red-500">{formatCurrency(ozet.toplamBorc)}</p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-3 md:p-4">
            <p className="text-xs text-textSecondary">Asgari Ödeme</p>
            <p className="text-base md:text-xl font-bold text-orange-500">{formatCurrency(ozet.toplamAsgariOdeme)}</p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-3 md:p-4">
            <p className="text-xs text-textSecondary">Toplam Limit</p>
            <p className="text-base md:text-xl font-bold text-blue-500">{formatCurrency(ozet.toplamLimit)}</p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-3 md:p-4">
            <p className="text-xs text-textSecondary">Kalan Limit</p>
            <p className="text-base md:text-xl font-bold text-green-500">{formatCurrency(ozet.kalanLimit)}</p>
          </div>
        </div>
      )}

      {/* Kart Listesi */}
      {kartlar.length === 0 ? (
        <div className="text-center py-12 bg-surface border border-border rounded-lg">
          <CreditCard className="h-12 w-12 mx-auto text-textSecondary mb-4" />
          <p className="text-textSecondary">
            {showSilinmisler ? 'Silinmiş kart bulunmuyor' : 'Henüz kredi kartı eklenmemiş'}
          </p>
          {!showSilinmisler && (
            <Button onClick={() => openModal()} className="mt-4 bg-primary">
              <Plus className="h-4 w-4 mr-1" />
              İlk Kartı Ekle
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-3 md:gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {kartlar.map((kart) => (
            <div
              key={kart.id}
              className={`bg-surface border rounded-xl p-3 md:p-4 ${
                showSilinmisler ? 'border-red-200 bg-red-50' : 'border-border'
              }`}
            >
              {/* Kart Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-textPrimary">{kart.kartAdi}</h3>
                  <p className="text-sm text-textSecondary">{kart.bankaAdi}</p>
                  {kart.kartNumarasi && (
                    <p className="text-xs text-textSecondary">**** {kart.kartNumarasi}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  {showSilinmisler ? (
                    <Button size="sm" variant="ghost" onClick={() => handleGeriYukle(kart)}>
                      <RotateCcw className="h-4 w-4 text-green-500" />
                    </Button>
                  ) : (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openModal(kart)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(kart)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Borç Bilgileri */}
              <div className="space-y-2 mb-3">
                <div className="flex justify-between">
                  <span className="text-sm text-textSecondary">Güncel Borç:</span>
                  <span className="font-bold text-red-500">{formatCurrency(kart.guncelBorc || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-textSecondary">Asgari Ödeme:</span>
                  <span className="font-medium text-orange-500">{formatCurrency(kart.asgariOdeme || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-textSecondary">Limit:</span>
                  <span className="font-medium">{formatCurrency(kart.kartLimiti || 0)}</span>
                </div>
              </div>

              {/* Tarih Bilgileri */}
              <div className="flex gap-4 text-xs border-t border-border pt-3">
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3 text-textSecondary" />
                  <span className="text-textSecondary">Kesim: {kart.hesapKesimGunu}. gün</span>
                </div>
                <div className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3 text-red-500" />
                  <span className="text-red-500 font-medium">Son Ödeme: {kart.sonOdemeGunu}. gün</span>
                </div>
              </div>

              {/* Ekstre */}
              <div className="mt-3 pt-3 border-t border-border">
                {kart.ekstreUrl ? (
                  <div className="flex items-center justify-between">
                    <a
                      href={`${BACKEND_URL}${kart.ekstreUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      <Eye className="h-3 w-3" />
                      Ekstre Görüntüle
                    </a>
                    <label className="text-xs text-textSecondary hover:text-primary cursor-pointer flex items-center gap-1">
                      <Upload className="h-3 w-3" />
                      Güncelle
                      <input
                        type="file"
                        className="hidden"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => e.target.files[0] && handleUploadEkstre(kart.id, e.target.files[0])}
                      />
                    </label>
                  </div>
                ) : (
                  <label className="text-xs text-primary hover:underline cursor-pointer flex items-center gap-1 justify-center">
                    <Upload className="h-3 w-3" />
                    Ekstre Yükle
                    <input
                      type="file"
                      className="hidden"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => e.target.files[0] && handleUploadEkstre(kart.id, e.target.files[0])}
                    />
                  </label>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-bold">
                {editingKart ? 'Kredi Kartı Düzenle' : 'Yeni Kredi Kartı'}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Kart Adı *</label>
                  <Input
                    value={formData.kartAdi}
                    onChange={(e) => setFormData({ ...formData, kartAdi: e.target.value })}
                    placeholder="Örn: Akbank Axess"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Banka *</label>
                  <Select
                    value={formData.bankaAdi}
                    onValueChange={(val) => setFormData({ ...formData, bankaAdi: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Banka seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...new Set(bankalar.map(b => b.ad))].map((ad) => (
                        <SelectItem key={ad} value={ad}>{ad}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Son 4 Hane</label>
                  <Input
                    value={formData.kartNumarasi}
                    onChange={(e) => setFormData({ ...formData, kartNumarasi: e.target.value.slice(0, 4) })}
                    placeholder="1234"
                    maxLength={4}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Hesap Kesim Günü *</label>
                  <Select
                    value={formData.hesapKesimGunu}
                    onValueChange={(val) => setFormData({ ...formData, hesapKesimGunu: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Gün seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {gunler.map((gun) => (
                        <SelectItem key={gun} value={gun.toString()}>{gun}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Son Ödeme Günü *</label>
                  <Select
                    value={formData.sonOdemeGunu}
                    onValueChange={(val) => setFormData({ ...formData, sonOdemeGunu: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Gün seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {gunler.map((gun) => (
                        <SelectItem key={gun} value={gun.toString()}>{gun}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Kart Limiti (₺)</label>
                  <Input
                    type="number"
                    value={formData.kartLimiti}
                    onChange={(e) => setFormData({ ...formData, kartLimiti: e.target.value })}
                    placeholder="50000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Güncel Borç (₺)</label>
                  <Input
                    type="number"
                    value={formData.guncelBorc}
                    onChange={(e) => setFormData({ ...formData, guncelBorc: e.target.value })}
                    placeholder="0"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Asgari Ödeme (₺)</label>
                  <Input
                    type="number"
                    value={formData.asgariOdeme}
                    onChange={(e) => setFormData({ ...formData, asgariOdeme: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-2 p-4 border-t border-border">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                İptal
              </Button>
              <Button onClick={handleSubmit} className="flex-1 bg-primary">
                {editingKart ? 'Güncelle' : 'Kaydet'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
